// ResumeBuilder.jsx — FULL FIXED FILE
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PaywallPage from '../ATS&Paywall/PaywallPage';
import {
  Download, RefreshCw, Plus, Trash2, ChevronDown, ChevronUp,
  GripVertical, PenLine, X, Check, LayoutTemplate, Sparkles,
  AlignLeft, List, BookOpen, Copy, Eye
} from 'lucide-react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useLatexCompiler } from '../../hooks/useLatexCompiler';
import { useDebounce } from '../../hooks/useDebounce';
import { generateFullLatex } from '../../utils/latexGenerator';
import {
  INITIAL_RESUME_DATA, DEFAULT_SECTION_ORDER,
  STORAGE_KEYS, DEBOUNCE_DELAY
} from '../../utils/constants';
import DarkModeToggle from '../common/DarkModeToggle';
import {
  PersonalInfoForm, EducationForm, ExperienceForm,
  ProjectsForm, SkillsForm, CertificationsForm
} from '../forms/AllForms';
import SectionOrderManager from './SectionOrderManager';
import ResumePreview from './ResumePreview';

/* ════════════════════════════════════════════════════════════════════
   STYLES
════════════════════════════════════════════════════════════════════ */
const injectStyles = () => {
  if (document.getElementById('rb-styles')) return;
  const style = document.createElement('style');
  style.id = 'rb-styles';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,700;1,500&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

    :root {
      --rb-cream:       #FAF8F5;
      --rb-ink:         #1A1814;
      --rb-gold:        #C8973A;
      --rb-gold-lt:     #E8BF72;
      --rb-gold-bg:     rgba(200,151,58,0.07);
      --rb-sage:        #4A5E52;
      --rb-sage-bg:     rgba(74,94,82,0.09);
      --rb-border:      rgba(26,24,20,0.10);
      --rb-border-md:   rgba(26,24,20,0.16);
      --rb-card-bg:     #FFFFFF;
      --rb-muted:       rgba(26,24,20,0.40);
      --rb-radius:      14px;
      --rb-radius-sm:   9px;
      --rb-shadow:      0 2px 24px rgba(26,24,20,0.07);
      --rb-shadow-lg:   0 8px 40px rgba(26,24,20,0.13);

      --bb-accent:      #5B4FCF;
      --bb-accent-bg:   rgba(91,79,207,0.07);
      --bb-accent-bdr:  rgba(91,79,207,0.20);
      --bb-red:         #C0392B;
      --bb-red-bg:      rgba(192,57,43,0.07);
    }

    *, *::before, *::after { box-sizing: border-box; }

    .rb-root {
      display: flex;
      min-height: 100vh;
      background: var(--rb-cream);
      font-family: 'DM Sans', sans-serif;
      color: var(--rb-ink);
    }

    .rb-left {
      width: 100%;
      max-width: 700px;
      display: flex;
      flex-direction: column;
      border-right: 1px solid var(--rb-border);
      background: var(--rb-cream);
      position: relative;
      z-index: 1;
      flex-shrink: 0;
    }
    .rb-left::before {
      content: '';
      position: fixed;
      inset: 0;
      width: 700px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
      pointer-events: none;
      z-index: 0;
    }

    .rb-progress-strip {
      height: 3px;
      background: linear-gradient(90deg, var(--rb-gold) 0%, var(--rb-gold-lt) 60%, transparent 100%);
    }

    .rb-header {
      position: sticky;
      top: 0;
      z-index: 20;
      padding: 18px 32px 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: rgba(250,248,245,0.90);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border-bottom: 1px solid var(--rb-border);
    }
    .rb-logo {
      font-family: 'Playfair Display', Georgia, serif;
      font-size: 1.5rem;
      font-weight: 700;
      color: var(--rb-ink);
      letter-spacing: -0.5px;
      display: flex;
      align-items: baseline;
      line-height: 1;
    }
    .rb-logo-dot {
      width: 7px; height: 7px;
      border-radius: 50%;
      background: var(--rb-gold);
      display: inline-block;
      margin-left: 2px;
      position: relative;
      top: -3px;
    }
    .rb-tagline {
      font-size: 0.7rem;
      font-weight: 400;
      color: var(--rb-sage);
      letter-spacing: 0.07em;
      text-transform: uppercase;
      margin-top: 3px;
    }
    .rb-header-actions { display: flex; align-items: center; gap: 8px; }

    .rb-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 16px;
      font-family: 'DM Sans', sans-serif;
      font-size: 0.8rem;
      font-weight: 600;
      border-radius: 50px;
      cursor: pointer;
      transition: all 0.18s ease;
      border: none;
      text-decoration: none;
      white-space: nowrap;
      line-height: 1;
    }
    .rb-btn-primary { background: var(--rb-ink); color: var(--rb-cream); }
    .rb-btn-primary:hover {
      background: var(--rb-gold); color: var(--rb-ink);
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(200,151,58,0.30);
    }
    .rb-btn-ghost {
      background: transparent; color: var(--rb-ink);
      border: 1px solid var(--rb-border-md);
    }
    .rb-btn-ghost:hover { background: rgba(26,24,20,0.05); border-color: rgba(26,24,20,0.22); }

    .rb-btn-preview {
      display: none;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      padding: 0;
      border-radius: 50%;
      background: var(--bb-accent-bg);
      color: var(--bb-accent);
      border: 1px solid var(--bb-accent-bdr);
      cursor: pointer;
      transition: all 0.18s ease;
      flex-shrink: 0;
    }
    .rb-btn-preview:hover {
      background: var(--bb-accent);
      color: #fff;
      transform: translateY(-1px);
      box-shadow: 0 4px 14px rgba(91,79,207,0.30);
    }
    .rb-btn-preview.active { background: var(--bb-accent); color: #fff; }

    .rb-preview-modal-overlay {
      display: none;
      position: fixed;
      inset: 0;
      z-index: 500;
      background: rgba(26,24,20,0.55);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      animation: rbFadeIn 0.22s ease;
    }
    .rb-preview-modal-overlay.visible { display: flex; align-items: flex-end; }
    .rb-preview-modal {
      width: 100%;
      height: 92vh;
      background: var(--rb-card-bg);
      border-radius: 22px 22px 0 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      animation: rbSlideUp 0.28s cubic-bezier(0.32, 0.72, 0, 1);
      box-shadow: 0 -8px 48px rgba(26,24,20,0.18);
    }
    .rb-preview-modal-header {
      padding: 14px 20px 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid var(--rb-border);
      background: var(--rb-cream);
      flex-shrink: 0;
    }
    .rb-preview-modal-drag {
      position: absolute;
      top: 8px;
      left: 50%;
      transform: translateX(-50%);
      width: 36px; height: 4px;
      border-radius: 2px;
      background: var(--rb-border-md);
    }
    .rb-preview-modal-title {
      font-family: 'Playfair Display', serif;
      font-size: 1rem; font-weight: 700; color: var(--rb-ink);
      display: flex; align-items: center; gap: 8px;
    }
    .rb-preview-modal-title-badge {
      width: 22px; height: 22px; border-radius: 6px;
      background: var(--bb-accent); color: #fff;
      display: flex; align-items: center; justify-content: center;
      font-size: 0.58rem; font-weight: 700; letter-spacing: 0.04em;
    }
    .rb-preview-modal-status {
      font-size: 0.68rem;
      font-family: 'DM Mono', monospace;
      color: var(--rb-muted);
      padding: 3px 9px;
      border-radius: 50px;
      background: var(--rb-gold-bg);
      border: 1px solid rgba(200,151,58,0.18);
    }
    .rb-preview-modal-status.compiling {
      background: var(--bb-accent-bg);
      border-color: var(--bb-accent-bdr);
      color: var(--bb-accent);
    }
    .rb-preview-modal-close {
      width: 30px; height: 30px;
      border-radius: 50%;
      background: rgba(26,24,20,0.06);
      border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      color: var(--rb-ink); transition: background 0.15s; flex-shrink: 0;
    }
    .rb-preview-modal-close:hover { background: rgba(26,24,20,0.12); }
    .rb-preview-modal-body {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      position: relative;
      background: #525659;
      -webkit-overflow-scrolling: touch;
    }

    .rb-pdf-canvas-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 16px 8px 32px;
      gap: 12px;
      min-height: 100%;
    }
    .rb-pdf-canvas-loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 14px;
      color: rgba(255,255,255,0.75);
      font-family: 'DM Sans', sans-serif;
      font-size: 0.85rem;
      padding: 60px 20px;
      text-align: center;
      min-height: 300px;
    }
    .rb-pdf-spinner {
      width: 36px; height: 36px;
      border: 3px solid rgba(255,255,255,0.2);
      border-top-color: #fff;
      border-radius: 50%;
      animation: rbSpin 0.8s linear infinite;
    }
    .rb-pdf-canvas-error {
      display: flex; flex-direction: column;
      align-items: center; gap: 10px;
      padding: 40px 24px;
      color: rgba(255,255,255,0.75);
      font-size: 0.82rem; text-align: center;
      font-family: 'DM Sans', sans-serif;
    }
    .rb-pdf-page-counter {
      font-family: 'DM Mono', monospace;
      font-size: 0.62rem;
      color: rgba(255,255,255,0.4);
      margin-top: 6px; text-align: center;
    }

    .rb-desktop-preview-wrap {
      position: relative;
      width: 100%; height: 100%;
    }
    .rb-desktop-preview-wrap iframe {
      width: 100%; height: 100%; border: none;
    }
    .rb-preview-shield {
      position: absolute;
      inset: 0;
      z-index: 10;
      cursor: default;
    }

    @keyframes rbFadeIn  { from { opacity: 0 } to { opacity: 1 } }
    @keyframes rbSlideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
    @keyframes rbSpin    { to { transform: rotate(360deg) } }

    .rb-scroll {
      flex: 1;
      overflow-y: auto;
      padding: 24px 32px 80px;
      position: relative;
      z-index: 1;
      scrollbar-width: thin;
      scrollbar-color: rgba(26,24,20,0.11) transparent;
    }
    .rb-scroll::-webkit-scrollbar { width: 4px; }
    .rb-scroll::-webkit-scrollbar-track { background: transparent; }
    .rb-scroll::-webkit-scrollbar-thumb { background: rgba(26,24,20,0.11); border-radius: 4px; }

    .rb-banner {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 10px 15px; border-radius: var(--rb-radius-sm);
      font-size: 0.79rem; font-weight: 500; margin-bottom: 10px; line-height: 1.5;
    }
    .rb-banner-save { background: var(--rb-sage-bg); color: var(--rb-sage); border: 1px solid rgba(74,94,82,0.18); }
    .rb-banner-tip  { background: var(--rb-gold-bg); color: #9A6F1E; border: 1px solid rgba(200,151,58,0.20); }
    .rb-banner-icon { font-size: 0.9rem; flex-shrink: 0; margin-top: 1px; }

    .rb-divider { display: flex; align-items: center; gap: 12px; margin: 28px 0 18px; }
    .rb-divider-line { flex: 1; height: 1px; background: var(--rb-border); }
    .rb-divider-label {
      font-family: 'Playfair Display', serif;
      font-size: 0.68rem; font-weight: 500;
      letter-spacing: 0.15em; text-transform: uppercase;
      color: var(--rb-muted); white-space: nowrap;
    }

    .rb-card-wrap {
      background: var(--rb-card-bg);
      border: 1px solid var(--rb-border);
      border-radius: var(--rb-radius);
      box-shadow: var(--rb-shadow);
      margin-bottom: 14px; overflow: hidden;
      transition: box-shadow 0.2s;
    }
    .rb-card-wrap:hover { box-shadow: 0 4px 32px rgba(26,24,20,0.09); }
    .rb-card-header {
      padding: 13px 18px 12px; border-bottom: 1px solid var(--rb-border);
      display: flex; align-items: center; gap: 10px;
    }
    .rb-card-badge {
      width: 28px; height: 28px; border-radius: 8px;
      background: var(--rb-ink); color: var(--rb-cream);
      display: flex; align-items: center; justify-content: center;
      font-size: 0.62rem; font-weight: 700;
      letter-spacing: 0.04em; text-transform: uppercase; flex-shrink: 0;
    }
    .rb-card-title {
      font-family: 'Playfair Display', serif;
      font-size: 0.96rem; font-weight: 700; color: var(--rb-ink); flex: 1;
    }

    .rb-right { flex: 1; position: sticky; top: 0; height: 100vh; overflow: hidden; }

    .rb-mobile-preview { display: none; }

    @media (max-width: 1024px) {
      .rb-root { flex-direction: column; }
      .rb-left { max-width: 100%; border-right: none; border-bottom: 1px solid var(--rb-border); }
      .rb-left::before { width: 100%; }
      .rb-right { display: none; }
      .rb-btn-preview { display: inline-flex; }
      .rb-mobile-preview {
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        gap: 12px; padding: 28px 20px;
        background: var(--rb-card-bg);
        border-top: 1px solid var(--rb-border);
        text-align: center;
      }
      .rb-mobile-preview-icon {
        width: 48px; height: 48px; border-radius: 14px;
        background: var(--rb-gold-bg); border: 1px solid rgba(200,151,58,0.25);
        display: flex; align-items: center; justify-content: center;
        font-size: 1.4rem;
      }
      .rb-mobile-preview-title {
        font-family: 'Playfair Display', serif;
        font-size: 1rem; font-weight: 700; color: var(--rb-ink);
      }
      .rb-mobile-preview-sub {
        font-size: 0.78rem; color: var(--rb-muted); line-height: 1.55; max-width: 280px;
      }
    }

    .bb-outer {
      background: var(--rb-card-bg);
      border: 1.5px solid var(--bb-accent-bdr);
      border-radius: var(--rb-radius);
      box-shadow: var(--rb-shadow);
      margin-bottom: 14px; overflow: hidden;
    }
    .bb-master-header {
      padding: 13px 18px 12px;
      border-bottom: 1.5px solid var(--bb-accent-bdr);
      display: flex; align-items: center; justify-content: space-between;
      background: var(--bb-accent-bg);
    }
    .bb-master-left { display: flex; align-items: center; gap: 10px; }
    .bb-badge {
      width: 28px; height: 28px; border-radius: 8px;
      background: var(--bb-accent); color: #fff;
      display: flex; align-items: center; justify-content: center;
      font-size: 0.62rem; font-weight: 700; letter-spacing: 0.04em; flex-shrink: 0;
    }
    .bb-master-title { font-family: 'Playfair Display', serif; font-size: 0.96rem; font-weight: 700; color: var(--bb-accent); }
    .bb-master-subtitle { font-size: 0.72rem; color: var(--rb-muted); margin-top: 1px; }
    .bb-toggle-btn {
      background: none; border: none; cursor: pointer; color: var(--bb-accent);
      display: flex; align-items: center; padding: 4px; border-radius: 6px; transition: background 0.15s;
    }
    .bb-toggle-btn:hover { background: rgba(91,79,207,0.10); }
    .bb-body { padding: 18px 20px 22px; }

    .bb-intro {
      display: flex; align-items: flex-start; gap: 12px;
      padding: 12px 15px; border-radius: var(--rb-radius-sm);
      background: var(--bb-accent-bg); border: 1px solid var(--bb-accent-bdr);
      margin-bottom: 20px;
    }
    .bb-intro-icon { color: var(--bb-accent); flex-shrink: 0; margin-top: 2px; }
    .bb-intro-text { font-size: 0.78rem; color: var(--rb-muted); line-height: 1.55; }
    .bb-intro-text strong { color: var(--rb-ink); }

    .bb-templates { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
    .bb-tpl-pill {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 5px 13px; border-radius: 50px; font-size: 0.75rem; font-weight: 600;
      border: 1px solid var(--rb-border-md); background: var(--rb-cream); color: var(--rb-ink);
      cursor: pointer; transition: all 0.16s;
    }
    .bb-tpl-pill:hover { border-color: var(--bb-accent); color: var(--bb-accent); background: var(--bb-accent-bg); }
    .bb-tpl-label {
      font-size: 0.68rem; font-weight: 600; text-transform: uppercase;
      letter-spacing: 0.08em; color: var(--rb-muted); margin-bottom: 8px;
    }

    .bb-section-block {
      border: 1px solid var(--rb-border); border-radius: var(--rb-radius-sm);
      margin-bottom: 14px; background: var(--rb-cream); overflow: hidden;
      transition: box-shadow 0.2s;
    }
    .bb-section-block:hover { box-shadow: 0 2px 16px rgba(26,24,20,0.08); }
    .bb-section-block-header {
      padding: 11px 14px 10px; display: flex; align-items: center; gap: 8px;
      border-bottom: 1px solid var(--rb-border);
      background: rgba(255,255,255,0.7);
      cursor: pointer; user-select: none;
    }
    .bb-section-block-header:hover { background: rgba(255,255,255,0.95); }
    .bb-drag-handle { color: var(--rb-muted); cursor: grab; display: flex; align-items: center; flex-shrink: 0; }
    .bb-section-name-input {
      flex: 1; font-family: 'DM Sans', sans-serif; font-size: 0.88rem; font-weight: 600;
      color: var(--rb-ink); background: transparent; border: none; outline: none; cursor: text;
    }
    .bb-section-name-input::placeholder { color: var(--rb-muted); font-weight: 400; }
    .bb-section-block-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
    .bb-icon-btn {
      width: 28px; height: 28px; display: flex; align-items: center; justify-content: center;
      border-radius: 7px; border: none; cursor: pointer;
      transition: background 0.15s, color 0.15s; background: transparent; color: var(--rb-muted);
    }
    .bb-icon-btn:hover { background: rgba(26,24,20,0.07); color: var(--rb-ink); }
    .bb-icon-btn.danger:hover { background: var(--bb-red-bg); color: var(--bb-red); }
    .bb-section-block-body { padding: 14px; }

    .bb-subitem {
      border: 1px solid var(--rb-border); border-radius: 9px;
      padding: 13px 14px; margin-bottom: 10px; background: #fff; position: relative;
    }
    .bb-subitem-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
    .bb-subitem-label {
      font-size: 0.7rem; font-weight: 600; text-transform: uppercase;
      letter-spacing: 0.09em; color: var(--rb-muted); display: flex; align-items: center; gap: 5px;
    }
    .bb-subitem-number {
      width: 18px; height: 18px; border-radius: 50%;
      background: var(--bb-accent-bg); color: var(--bb-accent);
      font-size: 0.62rem; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
    }

    .bb-field-grid { display: grid; gap: 8px; }
    .bb-field-grid-2 { grid-template-columns: 1fr 1fr; }
    .bb-field-grid-1 { grid-template-columns: 1fr; }
    .bb-field-group { display: flex; flex-direction: column; gap: 4px; }
    .bb-field-label {
      font-size: 0.7rem; font-weight: 600; color: var(--rb-muted);
      text-transform: uppercase; letter-spacing: 0.07em;
    }
    .bb-input, .bb-textarea {
      width: 100%; font-family: 'DM Sans', sans-serif; font-size: 0.83rem;
      color: var(--rb-ink); background: var(--rb-cream);
      border: 1px solid var(--rb-border-md); border-radius: var(--rb-radius-sm);
      padding: 8px 11px; outline: none; transition: border-color 0.15s, box-shadow 0.15s; resize: none;
    }
    .bb-input:focus, .bb-textarea:focus {
      border-color: var(--bb-accent);
      box-shadow: 0 0 0 3px rgba(91,79,207,0.10);
    }
    .bb-textarea { min-height: 72px; resize: vertical; line-height: 1.5; }

    .bb-bullet-list { display: flex; flex-direction: column; gap: 6px; }
    .bb-bullet-row  { display: flex; align-items: center; gap: 6px; }
    .bb-bullet-dot  { width: 6px; height: 6px; border-radius: 50%; background: var(--bb-accent); flex-shrink: 0; }
    .bb-bullet-input {
      flex: 1; font-family: 'DM Sans', sans-serif; font-size: 0.82rem; color: var(--rb-ink);
      background: var(--rb-cream); border: 1px solid var(--rb-border-md); border-radius: 7px;
      padding: 7px 10px; outline: none; transition: border-color 0.15s;
    }
    .bb-bullet-input:focus { border-color: var(--bb-accent); }

    .bb-add-row {
      display: flex; align-items: center; gap: 6px;
      padding: 7px 10px; border-radius: 8px;
      border: 1.5px dashed var(--rb-border-md); background: transparent;
      color: var(--rb-muted); font-family: 'DM Sans', sans-serif;
      font-size: 0.78rem; font-weight: 600; cursor: pointer; width: 100%;
      transition: all 0.16s; margin-top: 4px;
    }
    .bb-add-row:hover { border-color: var(--bb-accent); color: var(--bb-accent); background: var(--bb-accent-bg); }

    .bb-add-section-btn {
      display: flex; align-items: center; gap: 8px; width: 100%;
      padding: 11px 14px; border-radius: var(--rb-radius-sm);
      border: 1.5px dashed var(--bb-accent-bdr); background: var(--bb-accent-bg);
      color: var(--bb-accent); font-family: 'DM Sans', sans-serif;
      font-size: 0.82rem; font-weight: 600; cursor: pointer; transition: all 0.16s; margin-top: 6px;
    }
    .bb-add-section-btn:hover { background: rgba(91,79,207,0.12); border-color: var(--bb-accent); }

    .bb-type-chips { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 14px; }
    .bb-type-chip {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 4px 11px; border-radius: 50px; font-size: 0.72rem; font-weight: 600;
      border: 1px solid var(--rb-border-md); background: transparent; color: var(--rb-muted);
      cursor: pointer; transition: all 0.14s;
    }
    .bb-type-chip:hover, .bb-type-chip.active {
      border-color: var(--bb-accent); color: var(--bb-accent); background: var(--bb-accent-bg);
    }
    .bb-type-label {
      font-size: 0.68rem; font-weight: 600; text-transform: uppercase;
      letter-spacing: 0.08em; color: var(--rb-muted); margin-bottom: 6px;
    }

    .bb-collapsible { overflow: hidden; transition: max-height 0.3s ease, opacity 0.25s ease; }
    .bb-collapsible.open  { max-height: 9000px; opacity: 1; }
    .bb-collapsible.closed { max-height: 0; opacity: 0; }

    .bb-empty { text-align: center; padding: 24px 16px; color: var(--rb-muted); }
    .bb-empty-icon { font-size: 1.8rem; margin-bottom: 8px; }
    .bb-empty-text { font-size: 0.82rem; line-height: 1.5; }

    .bb-preview-chip {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 2px 8px; border-radius: 50px;
      font-family: 'DM Mono', monospace; font-size: 0.65rem;
      background: var(--bb-accent-bg); color: var(--bb-accent);
      border: 1px solid var(--bb-accent-bdr); margin-left: 8px;
    }

    @media (max-width: 640px) {
      .rb-header { padding: 13px 16px; }
      .rb-scroll  { padding: 18px 14px 80px; }
      .rb-btn .label-text { display: none; }
      .rb-btn { padding: 7px 11px; }
      .bb-field-grid-2 { grid-template-columns: 1fr; }
    }
  `;
  document.head.appendChild(style);
};

/* ════════════════════════════════════════════════════════════════════
   PDF.js LOADER
════════════════════════════════════════════════════════════════════ */
let pdfJsLoadPromise = null;
const loadPdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfJsLoadPromise) return pdfJsLoadPromise;

  pdfJsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = '';
      try {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      } catch (_) {}
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('Failed to load PDF.js'));
    document.head.appendChild(script);
  });

  return pdfJsLoadPromise;
};

const paintWatermark = (ctx, width, height) => {
  ctx.save();
  const text    = 'PREVIEW ONLY — typemycv.com';
  const fontSize = Math.max(14, width * 0.028);
  ctx.font      = `bold ${fontSize}px Arial, sans-serif`;
  ctx.fillStyle = 'rgba(180, 60, 60, 0.13)';
  ctx.textAlign = 'center';
  const stepX = width  * 0.55;
  const stepY = height * 0.18;
  for (let y = -stepY; y < height + stepY * 2; y += stepY) {
    for (let x = -stepX; x < width + stepX; x += stepX) {
      ctx.save();
      ctx.translate(x + stepX / 2, y);
      ctx.rotate(-Math.PI / 6);
      ctx.fillText(text, 0, 0);
      ctx.restore();
    }
  }
  const stampSize = Math.max(22, width * 0.045);
  ctx.font        = `900 ${stampSize}px Arial, sans-serif`;
  ctx.fillStyle   = 'rgba(180, 60, 60, 0.09)';
  ctx.textAlign   = 'center';
  ctx.save();
  ctx.translate(width / 2, height / 2);
  ctx.rotate(-Math.PI / 6);
  ctx.fillText('PREVIEW ONLY', 0, 0);
  ctx.restore();
  ctx.restore();
};

/* ════════════════════════════════════════════════════════════════════
   HELPERS & CONSTANTS
════════════════════════════════════════════════════════════════════ */
const uid = () => Math.random().toString(36).slice(2, 9);

const SECTION_META = {
  education:      { abbr: 'EDU', label: 'Education' },
  experience:     { abbr: 'EXP', label: 'Experience' },
  projects:       { abbr: 'PRJ', label: 'Projects' },
  skills:         { abbr: 'SKL', label: 'Skills' },
  certifications: { abbr: 'CRT', label: 'Certifications' },
};

const SUBITEM_TYPES = {
  entry: {
    label: 'Entry',
    icon: <BookOpen size={11} />,
    desc: 'Title · Organisation · Date · Description',
    defaultValues: { title: '', organisation: '', dateRange: '', description: '' }
  },
  bullets: {
    label: 'Bullet List',
    icon: <List size={11} />,
    desc: 'Title + bullet points',
    defaultValues: { title: '', bullets: [''] }
  },
  paragraph: {
    label: 'Paragraph',
    icon: <AlignLeft size={11} />,
    desc: 'Heading + free-text paragraph',
    defaultValues: { heading: '', paragraph: '' }
  },
};

const QUICK_TEMPLATES = [
  { label: 'Volunteering',  icon: '🤝', sectionTitle: 'Volunteer Experience',  type: 'entry' },
  { label: 'Publications',  icon: '📄', sectionTitle: 'Publications',           type: 'entry' },
  { label: 'Awards',        icon: '🏆', sectionTitle: 'Honours & Awards',       type: 'entry' },
  { label: 'Languages',     icon: '🌐', sectionTitle: 'Languages',              type: 'bullets' },
  { label: 'Interests',     icon: '✦',  sectionTitle: 'Interests & Hobbies',   type: 'paragraph' },
  { label: 'References',    icon: '👤', sectionTitle: 'References',             type: 'entry' },
];

const customSectionsToLatex = (sections) =>
  sections.map(sec => {
    if (!sec.title && sec.items.length === 0) return '';
    const lines = [`\\section{${sec.title}}`];
    sec.items.forEach(item => {
      if (item.type === 'entry') {
        lines.push(`\\resumeSubheading{${item.title || ''}}{${item.dateRange || ''}}{${item.organisation || ''}}{}`);
        if (item.description) {
          lines.push(`\\resumeItemListStart`);
          lines.push(`\\resumeItem{${item.description}}`);
          lines.push(`\\resumeItemListEnd`);
        }
      } else if (item.type === 'bullets') {
        if (item.title) lines.push(`\\textbf{${item.title}}`);
        lines.push(`\\resumeItemListStart`);
        (item.bullets || []).filter(Boolean).forEach(b => lines.push(`\\resumeItem{${b}}`));
        lines.push(`\\resumeItemListEnd`);
      } else if (item.type === 'paragraph') {
        if (item.heading) lines.push(`\\textbf{${item.heading}}\\\\[2pt]`);
        if (item.paragraph) lines.push(item.paragraph);
      }
    });
    return lines.join('\n');
  }).filter(Boolean).join('\n\n');

/* ════════════════════════════════════════════════════════════════════
   SUB-COMPONENTS
════════════════════════════════════════════════════════════════════ */
const SubItemEditor = ({ item, index, onUpdate, onRemove }) => {
  const updateField = (field, value) => onUpdate({ ...item, [field]: value });
  const updateBullet = (i, val) => {
    const bullets = [...(item.bullets || [''])];
    bullets[i] = val;
    onUpdate({ ...item, bullets });
  };
  const addBullet    = () => onUpdate({ ...item, bullets: [...(item.bullets || ['']), ''] });
  const removeBullet = (i) => {
    const bullets = (item.bullets || ['']).filter((_, idx) => idx !== i);
    onUpdate({ ...item, bullets: bullets.length ? bullets : [''] });
  };

  return (
    <div className="bb-subitem">
      <div className="bb-subitem-header">
        <span className="bb-subitem-label">
          <span className="bb-subitem-number">{index + 1}</span>
          {SUBITEM_TYPES[item.type]?.label ?? 'Entry'}
        </span>
        <button className="bb-icon-btn danger" onClick={onRemove} title="Remove entry">
          <Trash2 size={13} />
        </button>
      </div>
      {item.type === 'entry' && (
        <>
          <div className="bb-field-grid bb-field-grid-2" style={{ marginBottom: 8 }}>
            <div className="bb-field-group">
              <span className="bb-field-label">Title / Role</span>
              <input className="bb-input" value={item.title || ''} placeholder="e.g. Lead Organiser"
                onChange={e => updateField('title', e.target.value)} />
            </div>
            <div className="bb-field-group">
              <span className="bb-field-label">Organisation</span>
              <input className="bb-input" value={item.organisation || ''} placeholder="e.g. Red Cross"
                onChange={e => updateField('organisation', e.target.value)} />
            </div>
          </div>
          <div className="bb-field-grid bb-field-grid-1" style={{ marginBottom: 8 }}>
            <div className="bb-field-group">
              <span className="bb-field-label">Date / Period</span>
              <input className="bb-input" value={item.dateRange || ''} placeholder="e.g. Jun 2022 – Present"
                onChange={e => updateField('dateRange', e.target.value)} />
            </div>
          </div>
          <div className="bb-field-group">
            <span className="bb-field-label">Description</span>
            <textarea className="bb-textarea" value={item.description || ''}
              placeholder="Brief description. Use *text* for bold."
              onChange={e => updateField('description', e.target.value)} />
          </div>
        </>
      )}
      {item.type === 'bullets' && (
        <>
          <div className="bb-field-group" style={{ marginBottom: 10 }}>
            <span className="bb-field-label">Title / Heading</span>
            <input className="bb-input" value={item.title || ''} placeholder="e.g. Technical Skills"
              onChange={e => updateField('title', e.target.value)} />
          </div>
          <div className="bb-field-group">
            <span className="bb-field-label">Bullet Points</span>
            <div className="bb-bullet-list">
              {(item.bullets || ['']).map((b, i) => (
                <div className="bb-bullet-row" key={i}>
                  <span className="bb-bullet-dot" />
                  <input className="bb-bullet-input" value={b} placeholder={`Point ${i + 1}`}
                    onChange={e => updateBullet(i, e.target.value)} />
                  {(item.bullets || ['']).length > 1 && (
                    <button className="bb-icon-btn danger" onClick={() => removeBullet(i)}>
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button className="bb-add-row" onClick={addBullet}>
              <Plus size={12} /> Add bullet
            </button>
          </div>
        </>
      )}
      {item.type === 'paragraph' && (
        <>
          <div className="bb-field-group" style={{ marginBottom: 8 }}>
            <span className="bb-field-label">Heading</span>
            <input className="bb-input" value={item.heading || ''} placeholder="e.g. Personal Statement"
              onChange={e => updateField('heading', e.target.value)} />
          </div>
          <div className="bb-field-group">
            <span className="bb-field-label">Paragraph</span>
            <textarea className="bb-textarea" style={{ minHeight: 90 }} value={item.paragraph || ''}
              placeholder="Write freely here…"
              onChange={e => updateField('paragraph', e.target.value)} />
          </div>
        </>
      )}
    </div>
  );
};

const CustomSectionBlock = ({ section, onUpdate, onRemove, onDuplicate }) => {
  const [open, setOpen] = useState(true);
  const updateTitle = (e) => onUpdate({ ...section, title: e.target.value });
  const updateItem  = (i, updated) => onUpdate({ ...section, items: section.items.map((it, idx) => idx === i ? updated : it) });
  const removeItem  = (i) => onUpdate({ ...section, items: section.items.filter((_, idx) => idx !== i) });
  const addItem     = (type) => {
    const newItem = { id: uid(), type, ...SUBITEM_TYPES[type].defaultValues };
    onUpdate({ ...section, items: [...section.items, newItem] });
  };

  return (
    <div className="bb-section-block">
      <div className="bb-section-block-header" onClick={() => setOpen(o => !o)}>
        <span className="bb-drag-handle" onClick={e => e.stopPropagation()}><GripVertical size={15} /></span>
        <input className="bb-section-name-input" value={section.title}
          placeholder="Section Title (e.g. Volunteering)" onChange={updateTitle}
          onClick={e => e.stopPropagation()} />
        {section.items.length > 0 && (
          <span className="bb-preview-chip">{section.items.length} {section.items.length === 1 ? 'entry' : 'entries'}</span>
        )}
        <div className="bb-section-block-actions" onClick={e => e.stopPropagation()}>
          <button className="bb-icon-btn" title="Duplicate section" onClick={onDuplicate}><Copy size={13} /></button>
          <button className="bb-icon-btn danger" title="Delete section" onClick={onRemove}><Trash2 size={13} /></button>
          <span style={{ color: 'var(--rb-muted)', display: 'flex', alignItems: 'center' }}>
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </span>
        </div>
      </div>
      <div className={`bb-collapsible ${open ? 'open' : 'closed'}`}>
        <div className="bb-section-block-body">
          <div style={{ marginBottom: 12 }}>
            <div className="bb-type-label">Entry format</div>
            <div className="bb-type-chips">
              {Object.entries(SUBITEM_TYPES).map(([key, meta]) => (
                <button key={key}
                  className={`bb-type-chip${section.activeType === key ? ' active' : ''}`}
                  onClick={() => onUpdate({ ...section, activeType: key })} title={meta.desc}>
                  {meta.icon}{meta.label}
                </button>
              ))}
            </div>
          </div>
          {section.items.length === 0 ? (
            <div className="bb-empty">
              <div className="bb-empty-icon">✦</div>
              <div className="bb-empty-text">No entries yet. Add your first one below.</div>
            </div>
          ) : (
            section.items.map((item, i) => (
              <SubItemEditor key={item.id} item={item} index={i}
                onUpdate={(updated) => updateItem(i, updated)}
                onRemove={() => removeItem(i)} />
            ))
          )}
          <button className="bb-add-row" onClick={() => addItem(section.activeType || 'entry')}>
            <Plus size={13} /> Add {SUBITEM_TYPES[section.activeType || 'entry']?.label ?? 'Entry'}
          </button>
        </div>
      </div>
    </div>
  );
};

const BespokeBlocks = ({ sections, onChange }) => {
  const [open, setOpen] = useState(false);
  const addSection = (title = '', activeType = 'entry') => {
    onChange([...sections, { id: uid(), title, items: [], activeType }]);
    if (!open) setOpen(true);
  };
  const applyTemplate    = (tpl) => addSection(tpl.sectionTitle, tpl.type);
  const updateSection    = (i, updated) => onChange(sections.map((s, idx) => idx === i ? updated : s));
  const removeSection    = (i) => onChange(sections.filter((_, idx) => idx !== i));
  const duplicateSection = (i) => {
    const copy = { ...sections[i], id: uid(), title: sections[i].title + ' (Copy)',
      items: sections[i].items.map(it => ({ ...it, id: uid() })) };
    const next = [...sections]; next.splice(i + 1, 0, copy); onChange(next);
  };

  return (
    <div className="bb-outer">
      <div className="bb-master-header">
        <div className="bb-master-left">
          <div className="bb-badge">AB</div>
          <div>
            <div className="bb-master-title">Add Your Own Blocks</div>
            <div className="bb-master-subtitle">Custom sections tailored to your story</div>
          </div>
        </div>
        <button className="bb-toggle-btn" onClick={() => setOpen(o => !o)}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>
      <div className={`bb-collapsible ${open ? 'open' : 'closed'}`}>
        <div className="bb-body">
          <div className="bb-intro">
            <span className="bb-intro-icon"><Sparkles size={14} /></span>
            <div className="bb-intro-text">
              <strong>Build sections that standard templates don't offer.</strong>{' '}
              Add entries with titles &amp; descriptions, bullet-point lists, or free-form paragraphs.
              Every block compiles directly into your PDF resume.
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <div className="bb-tpl-label">Quick-start templates</div>
            <div className="bb-templates">
              {QUICK_TEMPLATES.map(tpl => (
                <button key={tpl.label} className="bb-tpl-pill" onClick={() => applyTemplate(tpl)}>
                  <span>{tpl.icon}</span>{tpl.label}
                </button>
              ))}
            </div>
          </div>
          {sections.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div className="bb-tpl-label">
                Your custom sections
                <span className="bb-preview-chip" style={{ marginLeft: 8 }}>{sections.length}</span>
              </div>
              {sections.map((sec, i) => (
                <CustomSectionBlock key={sec.id} section={sec}
                  onUpdate={(updated) => updateSection(i, updated)}
                  onRemove={() => removeSection(i)}
                  onDuplicate={() => duplicateSection(i)} />
              ))}
            </div>
          )}
          <button className="bb-add-section-btn" onClick={() => addSection()}>
            <Plus size={14} /> Add a blank custom section
          </button>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════
   PDF CANVAS VIEWER
════════════════════════════════════════════════════════════════════ */
const PdfCanvasViewer = ({ pdfUrl, isCompiling }) => {
  const containerRef = useRef(null);
  const renderIdRef  = useRef(0);
  const [status, setStatus] = useState('idle');

  const renderPdf = useCallback(async (url) => {
    if (!url) return;
    const myId = ++renderIdRef.current;
    setStatus('loading');
    if (containerRef.current) containerRef.current.innerHTML = '';
    try {
      const resp   = await fetch(url);
      const buffer = await resp.arrayBuffer();
      if (renderIdRef.current !== myId) return;
      const pdfjsLib = await loadPdfJs();
      if (renderIdRef.current !== myId) return;
      const pdf = await pdfjsLib.getDocument({
        data: buffer,
        disableWorker: false,
        cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/',
        cMapPacked: true,
      }).promise;
      if (renderIdRef.current !== myId) return;
      const totalPages = pdf.numPages;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const containerW = (containerRef.current?.clientWidth || window.innerWidth) - 24;
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (renderIdRef.current !== myId) return;
        const page = await pdf.getPage(pageNum);
        const nativeVP = page.getViewport({ scale: 1 });
        const scale = (containerW / nativeVP.width) * dpr;
        const viewport = page.getViewport({ scale });
        const cssW = containerW;
        const cssH = Math.floor(viewport.height / dpr);
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'display:flex;flex-direction:column;align-items:center;width:100%;margin-bottom:14px;';
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.cssText = `width:${cssW}px;height:${cssH}px;display:block;border-radius:4px;` +
          'box-shadow:0 4px 24px rgba(0,0,0,0.35);background:#fff;';
        const label = document.createElement('div');
        label.style.cssText = 'font-family:DM Mono,monospace;font-size:0.62rem;' +
          'color:rgba(255,255,255,0.38);margin-top:6px;text-align:center;';
        label.textContent = `Page ${pageNum} of ${totalPages}`;
        wrapper.appendChild(canvas);
        wrapper.appendChild(label);
        if (!containerRef.current) return;
        containerRef.current.appendChild(wrapper);
        const ctx = canvas.getContext('2d');
        try {
          await page.render({ canvasContext: ctx, viewport }).promise;
        } catch (e) {
          if (e?.name === 'RenderingCancelledException') return;
          throw e;
        }
        paintWatermark(ctx, canvas.width, canvas.height);
        if (pageNum === 1) setStatus('rendering');
      }
      if (renderIdRef.current === myId) setStatus('done');
    } catch (err) {
      console.error('[PdfCanvasViewer]', err);
      if (renderIdRef.current === myId) setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (pdfUrl) renderPdf(pdfUrl);
  }, [pdfUrl, renderPdf]);

  if (isCompiling) {
    return (
      <div className="rb-pdf-canvas-loading">
        <div className="rb-pdf-spinner" />
        <span>Compiling your resume…</span>
      </div>
    );
  }
  if (status === 'loading') {
    return (
      <div className="rb-pdf-canvas-loading">
        <div className="rb-pdf-spinner" />
        <span>Rendering preview…</span>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="rb-pdf-canvas-error">
        <span style={{ fontSize: '1.8rem' }}>⚠️</span>
        <span>Preview failed. Close and reopen the preview to try again.</span>
      </div>
    );
  }
  if (status === 'idle') {
    return (
      <div className="rb-pdf-canvas-loading">
        <span style={{ fontSize: '2rem' }}>📄</span>
        <span>Fill in your details to see a live preview.</span>
      </div>
    );
  }
  return <div ref={containerRef} className="rb-pdf-canvas-wrap" />;
};

const WatermarkedDesktopPreview = ({ pdfUrl, isCompiling, error }) => {
  return (
    <div className="rb-desktop-preview-wrap">
      <ResumePreview pdfUrl={pdfUrl} isCompiling={isCompiling} error={error} />
      <div
        className="rb-preview-shield"
        onContextMenu={e => e.preventDefault()}
        title="This is a preview. Download after payment for the clean PDF."
      />
    </div>
  );
};

const MobilePreviewModal = ({ isOpen, onClose, pdfUrl, isCompiling, error }) => {
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className={`rb-preview-modal-overlay${isOpen ? ' visible' : ''}`} onClick={handleBackdropClick}>
      <div className="rb-preview-modal">
        <div style={{ position: 'relative', height: 0 }}>
          <div className="rb-preview-modal-drag" />
        </div>
        <div className="rb-preview-modal-header" style={{ position: 'relative', paddingTop: 20 }}>
          <div className="rb-preview-modal-title">
            <div className="rb-preview-modal-title-badge">PDF</div>
            Resume Preview
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isCompiling && <span className="rb-preview-modal-status compiling">Compiling…</span>}
            {!isCompiling && pdfUrl && <span className="rb-preview-modal-status">Live</span>}
            <button className="rb-preview-modal-close" onClick={onClose} aria-label="Close preview">
              <X size={15} />
            </button>
          </div>
        </div>
        <div className="rb-preview-modal-body">
          <PdfCanvasViewer pdfUrl={pdfUrl} isCompiling={isCompiling} />
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════════════ */
const ResumeBuilder = () => {
  useEffect(() => { injectStyles(); }, []);

  const [resumeData, setResumeData]     = useLocalStorage(STORAGE_KEYS.RESUME_DATA, INITIAL_RESUME_DATA);
  const [sectionOrder, setSectionOrder] = useLocalStorage(STORAGE_KEYS.SECTION_ORDER, DEFAULT_SECTION_ORDER);
  const [sectionOrderOpen, setSectionOrderOpen] = useState(false);
  const [showPaywall, setShowPaywall]   = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);

  const BESPOKE_KEY = (STORAGE_KEYS.RESUME_DATA ?? 'resumeData') + '_bespoke';
  const [customSections, setCustomSections] = useLocalStorage(BESPOKE_KEY, []);

  const pdfUrlRef      = useRef(null);
  const latexSourceRef = useRef(null);
  const [isReady, setIsReady]           = useState(false);
  const [compileError, setCompileError] = useState(null);

  const { pdfUrl, isCompiling: latexCompiling, error, compile, cleanup } = useLatexCompiler();

  useEffect(() => {
    if (pdfUrl) { pdfUrlRef.current = pdfUrl; setIsReady(true); setCompileError(null); }
    if (error)  { setCompileError(error); setIsReady(false); }
  }, [pdfUrl, error]);

  useDebounce(
    () => {
      const base = generateFullLatex(resumeData, sectionOrder);
      const customLatex = customSectionsToLatex(customSections);
      const merged = customLatex
        ? base.replace('\\end{document}', `\n% --- Bespoke Blocks ---\n${customLatex}\n\n\\end{document}`)
        : base;
      latexSourceRef.current = merged;
      compile(merged);
    },
    DEBOUNCE_DELAY,
    [resumeData, sectionOrder, customSections]
  );

  useEffect(() => { return () => cleanup(); }, [cleanup]);

  const clearFormData = () => {
    localStorage.removeItem(STORAGE_KEYS.RESUME_DATA);
    localStorage.removeItem(STORAGE_KEYS.SECTION_ORDER);
    localStorage.removeItem(BESPOKE_KEY);
    setResumeData(INITIAL_RESUME_DATA);
    setSectionOrder(DEFAULT_SECTION_ORDER);
    setCustomSections([]);
    pdfUrlRef.current = null;
    latexSourceRef.current = null;
    setIsReady(false);
  };

  const getResumeText = () => {
    const { personalInfo, education = [], experience = [], projects = [], skills, certifications = [] } = resumeData;
    return [
      personalInfo?.fullName, personalInfo?.email, personalInfo?.phone, personalInfo?.linkedin,
      ...(education.map(e => `${e.degree} ${e.institution} ${e.description || ''}`)),
      ...(experience.map(e => `${e.position} ${e.company} ${e.description || ''}`)),
      ...(projects.map(p => `${p.name} ${p.description || ''}`)),
      skills?.technical, skills?.soft,
      ...(certifications.map(c => `${c.name} ${c.issuer || ''}`)),
    ].filter(Boolean).join(' ');
  };

  // ── FIX 1: This was named getPdfUrlAfterPayment before but prop was getLatexSource
  // Now correctly returns the latex string from the ref
  const getLatexSource = () => latexSourceRef.current;

  return (
    <div className="rb-root">
      <div className="rb-left">
        <div className="rb-progress-strip" />
        <div className="rb-header">
          <div>
            <div className="rb-logo">TypeMyCv<span className="rb-logo-dot" /></div>
            <div className="rb-tagline">Recruiter-approved, in minutes</div>
          </div>
          <div className="rb-header-actions">
            <DarkModeToggle />
            <button
              className={`rb-btn-preview${showMobilePreview ? ' active' : ''}`}
              onClick={() => setShowMobilePreview(v => !v)}
              title="Preview resume" aria-label="Preview resume"
            >
              <Eye size={16} />
            </button>
            {isReady && (
              <button onClick={() => setShowPaywall(true)} className="rb-btn rb-btn-primary">
                <Download size={14} />
                <span className="label-text">Download</span>
              </button>
            )}
            {latexCompiling && (
              <span style={{ fontSize: '0.72rem', color: 'var(--rb-muted)', fontFamily: "'DM Mono', monospace" }}>
                Compiling…
              </span>
            )}
            <button onClick={clearFormData} className="rb-btn rb-btn-ghost" title="Clear all saved data">
              <RefreshCw size={14} />
              <span className="label-text">Reset</span>
            </button>
          </div>
        </div>

        <div className="rb-scroll">
          <div className="rb-banner rb-banner-save">
            <span className="rb-banner-icon">💾</span>
            <span><strong>Auto-Save:</strong> All data is persisted automatically as you type.</span>
          </div>
          <div className="rb-banner rb-banner-tip">
            <span className="rb-banner-icon">✦</span>
            <span><strong>Tip:</strong> Use <code>*text*</code> for <strong>bold</strong> formatting in any text field.</span>
          </div>

          <SectionOrderManager sectionOrder={sectionOrder} setSectionOrder={setSectionOrder}
            isOpen={sectionOrderOpen} setIsOpen={setSectionOrderOpen} />

          <div className="rb-divider">
            <div className="rb-divider-line" />
            <span className="rb-divider-label">Personal Details</span>
            <div className="rb-divider-line" />
          </div>

          <div className="rb-card-wrap">
            <div className="rb-card-header">
              <div className="rb-card-badge">YOU</div>
              <span className="rb-card-title">Personal Information</span>
            </div>
            <div className="rb-card-body">
              <PersonalInfoForm data={resumeData.personalInfo}
                onChange={(data) => setResumeData({ ...resumeData, personalInfo: data })} />
            </div>
          </div>

          <div className="rb-divider">
            <div className="rb-divider-line" />
            <span className="rb-divider-label">Standard Sections</span>
            <div className="rb-divider-line" />
          </div>

          {sectionOrder.map(sectionType => renderSection(sectionType))}

          <div className="rb-divider">
            <div className="rb-divider-line" />
            <span className="rb-divider-label">Personalized Slots</span>
            <div className="rb-divider-line" />
          </div>

          <BespokeBlocks sections={customSections} onChange={setCustomSections} />
        </div>
      </div>

      <div className="rb-right">
        <WatermarkedDesktopPreview
          pdfUrl={pdfUrl}
          isCompiling={latexCompiling}
          error={compileError}
        />
      </div>

      <div className="rb-mobile-preview">
        <div className="rb-mobile-preview-icon">📄</div>
        <div className="rb-mobile-preview-title">
          {latexCompiling ? 'Building your resume…' : isReady ? 'Your resume is ready!' : 'Fill in your details above'}
        </div>
        <div className="rb-mobile-preview-sub">
          {isReady
            ? 'Tap the Download button above to check your ATS score and get your PDF.'
            : 'Complete your details and your resume will compile automatically.'}
        </div>
        {isReady && (
          <button onClick={() => setShowPaywall(true)} className="rb-btn rb-btn-primary" style={{ marginTop: 4 }}>
            <Download size={14} /> Download Resume
          </button>
        )}
      </div>

      <MobilePreviewModal
        isOpen={showMobilePreview}
        onClose={() => setShowMobilePreview(false)}
        pdfUrl={pdfUrl}
        isCompiling={latexCompiling}
        error={compileError}
      />

      {/* ── FIX 2: Paywall now receives getLatexSource correctly ── */}
      {showPaywall && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 999, overflowY: 'auto' }}>
          <PaywallPage
            getLatexSource={getLatexSource}
            resumeText={getResumeText()}
            onBack={() => setShowPaywall(false)}
          />
        </div>
      )}
    </div>
  );

  function renderSection(sectionType) {
    const meta = SECTION_META[sectionType];
    let form = null;
    switch (sectionType) {
      case 'education':
        form = <EducationForm key="education" data={resumeData.education}
          onChange={(data) => setResumeData({ ...resumeData, education: data })} />; break;
      case 'experience':
        form = <ExperienceForm key="experience" data={resumeData.experience}
          onChange={(data) => setResumeData({ ...resumeData, experience: data })} />; break;
      case 'projects':
        form = <ProjectsForm key="projects" data={resumeData.projects}
          onChange={(data) => setResumeData({ ...resumeData, projects: data })} />; break;
      case 'skills':
        form = <SkillsForm key="skills" data={resumeData.skills}
          onChange={(data) => setResumeData({ ...resumeData, skills: data })} />; break;
      case 'certifications':
        form = <CertificationsForm key="certifications" data={resumeData.certifications}
          onChange={(data) => setResumeData({ ...resumeData, certifications: data })} />; break;
      default: return null;
    }
    return (
      <div className="rb-card-wrap" key={sectionType}>
        <div className="rb-card-header">
          <div className="rb-card-badge">{meta?.abbr ?? sectionType.slice(0, 3).toUpperCase()}</div>
          <span className="rb-card-title">{meta?.label ?? sectionType}</span>
        </div>
        <div className="rb-card-body">{form}</div>
      </div>
    );
  }
};

export default ResumeBuilder;